import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { Link, useMatch } from "react-router";

import { IcoChevronDown, IcoEnvironmentDot } from "@/common/components/Icons";
import MenuDescription from "@/common/components/MenuDescription";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useEnvironmentColors } from "@/environment/hooks/useEnvironmentColors";
import { EnvironmentHeaderDTO } from "@reflag/shared/environmentAPI";
import { EnvironmentUrl } from "@reflag/shared/urls";

const MAX_ENVIRONMENTS_LIST = 4;

function EnvironmentItem({
  environment,
  isSelected,
  fullEnvUrl,
}: {
  environment: EnvironmentHeaderDTO;
  isSelected: boolean;
  fullEnvUrl: string;
}) {
  const colors = useEnvironmentColors();
  const selectedBg = useColorModeValue("gray.100", "gray.850");
  const selectedColor = useColorModeValue("gray.900", "gray.50");
  const hoverText = useColorModeValue("gray.900", "gray.50");

  const envColor =
    colors[environment.isProduction ? "production" : "nonProduction"];
  const iconColor = envColor.icon;

  return (
    <Flex
      _hover={{ color: hoverText }}
      align="center"
      as={Link}
      borderRadius="md"
      className="group"
      color={isSelected ? "inherit" : "dimmed"}
      gap={1}
      px={2}
      py={1.5}
      sx={{
        textDecoration: "none",
        ...(isSelected && {
          bg: selectedBg,
          color: selectedColor,
        }),
      }}
      to={fullEnvUrl}
      transition="all 0.1s ease-in-out"
    >
      <IcoEnvironmentDot
        _groupHover={{ color: iconColor }}
        color={isSelected ? iconColor : "veryDimmed"}
        size="xs"
      />
      <Text
        flex="1"
        fontFamily="code"
        fontSize="sm"
        fontWeight="medium"
        isTruncated
      >
        {environment.name}
      </Text>
    </Flex>
  );
}

function EnvironmentDropdown({
  environments,
  currentEnv,
  getFullEnvUrl,
}: {
  environments: EnvironmentHeaderDTO[];
  currentEnv: EnvironmentHeaderDTO;
  getFullEnvUrl: (environment: EnvironmentHeaderDTO) => string;
}) {
  const colors = useEnvironmentColors();
  const selectedBg = useColorModeValue("gray.100", "gray.850");

  const currentEnvColor =
    colors[currentEnv.isProduction ? "production" : "nonProduction"];

  return (
    <Menu autoSelect={false} placement="top-start" matchWidth>
      <MenuButton
        as={Button}
        px={2}
        py={1.5}
        size="sm"
        variant="outlineOnHover"
        w="100%"
      >
        <Flex align="center" gap={1}>
          <IcoEnvironmentDot color={currentEnvColor.icon} size="xs" />
          <Text
            flex="1"
            fontFamily="code"
            fontSize="sm"
            fontWeight="medium"
            textAlign="left"
            isTruncated
          >
            {currentEnv.name}
          </Text>
          <IcoChevronDown color="dimmed" size="md" />
        </Flex>
      </MenuButton>
      <Portal>
        <MenuList maxH="300px" minW="max-content" overflowY="auto" zIndex={3}>
          <MenuDescription>Select environment:</MenuDescription>
          <MenuDivider my={0} />
          {environments.map((environment) => {
            const isSelected = currentEnv.id === environment.id;
            const envColor =
              colors[environment.isProduction ? "production" : "nonProduction"];

            return (
              <MenuItem
                key={environment.id}
                as={Link}
                bg={isSelected ? selectedBg : "transparent"}
                to={getFullEnvUrl(environment)}
              >
                <Flex align="center" gap={1}>
                  <IcoEnvironmentDot color={envColor.icon} size="xs" />
                  <Text fontFamily="code" fontSize="sm" fontWeight="medium">
                    {environment.name}
                  </Text>
                </Flex>
              </MenuItem>
            );
          })}
        </MenuList>
      </Portal>
    </Menu>
  );
}

export function EnvironmentPicker() {
  const { app: currentApp, env: currentEnv } = useEnvironment();
  const match = useMatch("/envs/:envId/*");

  if (!currentApp || !currentEnv) {
    return null;
  }

  const getFullEnvUrl = (environment: EnvironmentHeaderDTO) => {
    const envUrl = EnvironmentUrl(environment);
    return `${envUrl}/${match ? match.params["*"] : ""}`;
  };

  if (currentApp.environments.length > MAX_ENVIRONMENTS_LIST) {
    return (
      <EnvironmentDropdown
        currentEnv={currentEnv}
        environments={currentApp.environments}
        getFullEnvUrl={getFullEnvUrl}
      />
    );
  }

  return (
    <Flex direction="column" gap={1}>
      {currentApp.environments.map((environment) => (
        <EnvironmentItem
          key={environment.id}
          environment={environment}
          fullEnvUrl={getFullEnvUrl(environment)}
          isSelected={currentEnv.id === environment.id}
        />
      ))}
    </Flex>
  );
}
